/**
 * Sync one person to one device, and tell the backend what happened — both halves,
 * always, on every path.
 *
 * Four components grew their own copy of this loop (PersonDetailModal, PersonProfile,
 * VisitorRegistrationModal, PermissionsTab). Every copy reported successes via
 * mark-synced and reported failures to nobody, which is why the reconciliation ledger
 * never learned that a resident was missing a face on eight readers. Classification and
 * reporting live here so the copies cannot disagree again; the loop around it (parallel,
 * sequential, pausable) stays with each caller, since that part genuinely differs.
 */

import { DeviceIntegration, DeviceOperations, CommunicationMode } from '../integrations/devices';
import { reportDeviceSyncOutcome } from './deviceSyncOutcome';
import type { DeviceInfo } from '../services/deviceCommunication';

export interface SyncedDeviceEntry {
  device_id: string;
  device_name: string;
  source?: string;
}

export interface FailedDeviceEntry {
  device_id: string;
  device_name: string;
  error: string;
}

/**
 * Deliberately NOT a discriminated union: this project compiles with `strict: false`,
 * where narrowing on an `ok: true | false` discriminant does not hold, so callers would
 * have to cast at every push. Two explicitly-typed, mutually-exclusive rows instead —
 * exactly one of `synced` / `failed` is non-null.
 */
export interface SyncDeviceOutcome {
  ok: boolean;
  /** Row for the caller's successful-devices list. Null when the sync failed. */
  synced: SyncedDeviceEntry | null;
  /** Row for the caller's failed-devices list. Null when the sync succeeded. */
  failed: FailedDeviceEntry | null;
  /** Raw device result, when the call completed at all (absent if it threw). */
  result?: any;
}

export interface SyncDeviceAndReportInput {
  customerId: string;
  personId: string;
  device: DeviceInfo;
  /** Per-device payload — entrada devices get a shorter validUntil than saida/passagem. */
  payload: any;
  /** Zone display name, when the caller has a better one than the device's own. */
  deviceName?: string;
}

/**
 * Runs the sync, records the outcome, and returns the row the caller should push into
 * its successful/failed list. Never throws: a device failure is a RESULT, not an
 * exception, and a reporting failure must not take the sync down with it.
 */
export async function syncDeviceAndReport(input: SyncDeviceAndReportInput): Promise<SyncDeviceOutcome> {
  const { customerId, personId, device, payload } = input;
  const deviceName = input.deviceName || device.name || device.id;

  const report = (success: boolean, result: any, errorMessage?: string) =>
    Promise.resolve(
      reportDeviceSyncOutcome({
        customerId,
        personId,
        deviceId: device.id,
        device,
        success,
        source: result?.source,
        durationMs: result?.latencyMs,
        errorMessage,
      }),
    ).catch(() => undefined);

  let result: any;
  try {
    result = await DeviceIntegration.instance.execute(
      device,
      DeviceOperations.SYNC_PERSON,
      payload,
      CommunicationMode.HYBRID,
    );
  } catch (err: any) {
    const error = err?.message || 'Erro';
    await report(false, undefined, error);
    return { ok: false, synced: null, failed: { device_id: device.id, device_name: deviceName, error } };
  }

  if (result?.success) {
    await report(true, result);
    return {
      ok: true,
      synced: { device_id: device.id, device_name: deviceName, source: result.source },
      failed: null,
      result,
    };
  }

  const error = result?.error || 'Falha';
  await report(false, result, error);
  return {
    ok: false,
    synced: null,
    failed: { device_id: device.id, device_name: deviceName, error },
    result,
  };
}
